import { computed, ref, reactive, watch } from 'vue'
import { cloneDeep, debounce, isNil } from 'lodash'
import { ReactivityUtil } from '@/utils/Reactivity'
import useValidation from '@/hooks/useValidation'
import useBasket from '@/hooks/useBasket'
import useLogger from '@/hooks/useLogger'
import useConfirmation from '@/hooks/useConfirmation'
import useNotifications from '@/hooks/useNotifications'
import useTranslation from '@/hooks/useTranslation'
import { loading$ } from '@/services'
import { DateTimeUtil } from '@/utils/DateTime'

// Hooks
const { logError } = useLogger()
const { basket, updateOvpBasket, persistBasket, updateHealthquestionsForPerson } = useBasket()
const { confirm } = useConfirmation()
const { addNotification } = useNotifications()
const {
  i18n: {
    global: { t },
  },
} = useTranslation()

// data
const DEFAULT_DEBOUNCE = 5000 //  5 seconds
const personId = ref(undefined)
const tmpDoctors = reactive([])
const activeCase = reactive({})
let savedBatch = {}
let debouncedSaveOvp = (_savedBatch, _blocking) => {
  logError('No method registered to debouncedSaveOvp method')
}

const items = [
  { value: 'NO', text: 'form.no' },
  { value: 'YES', text: 'form.yes' },
]

// Computed
const isActiveModal = computed(() => {
  return !!activeCase.data
})

const activeCaseId = computed(() => {
  return activeCase.hqId ?? null
})

const activeCaseData = computed(() => {
  return activeCase.data ?? false
})

// Methods

/**
 * store data locally, and trigger saveOvp (timed or forced)
 * @param (Object) formData - payload
 * @param (Boolean) flush - flush the queue
 */
async function store(sectionId, formData, flush = false) {
  savedBatch[sectionId] = cloneDeep(formData)
  const __basket = await updateHealthquestionsForPerson(personId.value, savedBatch)
  persistBasket(__basket)
  if (flush) {
    saveOvp(__basket, true)
  } else {
    // @TODO Newly added, check correctnes
    debouncedSaveOvp.cancel()
    debouncedSaveOvp(__basket)
  }
}

/**
 * trigger backend-saveOvp
 * @param (Object) basket - state
 * @param (Boolean) blocking - block ui
 *
 * @return Promise
 */
async function saveOvp(__basket, blocking = false) {
  debouncedSaveOvp.cancel() // cancel debounce
  if (Object.keys(savedBatch).length === 0) return Promise.resolve()

  // @TODO should be after update is successful, right?
  savedBatch = {} // empty all savedBatch

  loading$.start({ blocking })

  try {
    await updateOvpBasket(__basket)
  } catch {
    addNotification(t('screen.healthquestions.failedToSaveHealthquestion'), 'error')
  }

  loading$.end()
}

function disrupt() {
  debouncedSaveOvp.cancel()
}

function resume() {
  saveOvp(basket, true)
  tmpDoctors.length = 0
}

async function forceSave() {
  // @note: for some reason, flush doesn't work on this debounce. basket is always empty
  await saveOvp(basket, true)
}

/**
 * When setting an healthquestion to NO, check beforehand, if there're any cases already filled in
 *
 * @param answer - which answer to check
 * @param keys - which keys to check
 * @param healthquestion - the healthquestion to check
 */
function confirmRemoval(answer, keys, healthquestion) {
  return new Promise((resolve, reject) => {
    if (answer !== 'NO') {
      resolve()
    } else {
      const askConfirmation =
        keys.filter(key => {
          return healthquestion[key].length > 0
        }).length > 0

      if (askConfirmation) {
        confirm(t('screen.healthquestions.deleteFilledData'))
          .then(() => resolve())
          .catch(() => reject())
      } else {
        resolve()
      }
    }
  })
}

function editActiveCase(data, index, hqId, type) {
  ReactivityUtil.reAssign(activeCase, { data, index, hqId, type })
}

function getDoctors() {
  const { basket: localBasket } = useBasket()
  const __doctors = cloneDeep(localBasket.doctors)

  tmpDoctors.forEach(doc => {
    const index = __doctors.findIndex(d => d.doctorId === doc.doctorId)

    if (index >= 0) {
      __doctors[index] = doc
    } else {
      __doctors.push(doc)
    }
  })

  return cloneDeep(__doctors)
}

function setPersonId(__personId) {
  personId.value = __personId
}

/* Health question checks */

const answeredYes = __hq => __hq.mainAnswer === 'YES'
const notAnswered = __hq => isNil(__hq.mainAnswer)
const isNotEmpty = arr => arr?.length > 0
const isPositiveNr = num => num > 0

const isAnsweredAndValid = (__hq, fieldNames) => {
  if (notAnswered(__hq)) return false
  if (!answeredYes(__hq)) return true
  return fieldNames.some(fieldName => isNotEmpty(__hq[fieldName]))
}

const hasValidSubAnswer = (__hq, fieldName) => {
  if (notAnswered(__hq)) return false
  if (!answeredYes(__hq)) return true
  if (isNil(__hq.subAnswers)) return false

  return !!__hq.subAnswers[fieldName]
}

const allDBCasesAnswered = __hq => {
  const subQuestions = [
    'dentalDenture',
    'dentalMissingTeeth',
    'dentalDeviateTeeth',
    'dentalGumDisease',
    'dentalAccident',
    'dentalAnomaly',
    'dentalRetention',
  ]
  return subQuestions.every(subQuestion => __hq.subAnswers && typeof __hq.subAnswers[subQuestion] === 'boolean')
}

const hasAnsweredWithValidDate = __hq => {
  if (notAnswered(__hq)) return false
  if (!answeredYes(__hq)) return true

  const { year, month } = __hq.subAnswers?.treatmentDate || {}
  if (!year || !month) return false

  const isInFuture = DateTimeUtil.isInFuture(`${year}-${month}-01`)
  return isNotEmpty(__hq.doctorIds) && !isInFuture
}

const dbCasesRequired = allAnswers => {
  const preconditions = _hq =>
    ['DB_TREATMENTS', 'DB_DEFECTS'].some(condition => {
      return condition === _hq.healthQuestionId && answeredYes(_hq)
    })
  return allAnswers.some(_hq => preconditions(_hq))
}

function healthQuestionConfig(hq, allAnswers) {
  switch (hq.healthQuestionId) {
    case 'BABY_WEIGHT':
      return isPositiveNr(hq.subAnswers?.birthWeight)
    case 'HEIGHT_WEIGHT':
      return isPositiveNr(hq.subAnswers?.height) && isPositiveNr(hq.subAnswers?.weight)
    case 'LIFESTYLE_MALE':
      return isAnsweredAndValid(hq, ['smokingConsumptions', 'alcoholConsumptions', 'drugConsumptions'])
    case 'LIFESTYLE_FEMALE':
      return isAnsweredAndValid(hq, ['smokingConsumptions', 'alcoholConsumptions', 'drugConsumptions'])
    case 'TREATMENTS':
      return isAnsweredAndValid(hq, ['treatmentCases'])
    case 'DRUGS':
      return isAnsweredAndValid(hq, ['medicines'])
    case 'DB_DEFECTS':
      return !notAnswered(hq)
    case 'IV':
      return !notAnswered(hq)
    case 'PF_CANCER':
      return !notAnswered(hq)
    case 'PF_FERTILITY':
      return !notAnswered(hq)
    case 'PF_ENDOMETRIOSIS':
      return !notAnswered(hq)
    case 'PF_MENSTRUATION':
      return !notAnswered(hq)
    case 'DB_TREATMENTS':
      return !notAnswered(hq)
    case 'PF_TREATMENTS':
      return !notAnswered(hq)
    case 'PF_PLANNEDTREATMENTS':
      return !notAnswered(hq)
    case 'JAW':
      return !notAnswered(hq)
    case 'PF_HEREDITARY_DISEASES':
      return hasValidSubAnswer(hq, 'hereditaryDisease')
    case 'DB_CASES':
      return !dbCasesRequired(allAnswers) || allDBCasesAnswered(hq)
    case 'DB_CONTROL':
      return hasAnsweredWithValidDate(hq)
    case 'DB_CONTROL_Y':
      return hasAnsweredWithValidDate(hq)
    case 'DB_HYGIENE':
      return hasAnsweredWithValidDate(hq)
    case 'DB_FILLINGS':
      return hasValidSubAnswer(hq, 'fillings')
    default:
      !notAnswered(hq)
  }
}

// Lifecycle hooks
debouncedSaveOvp = debounce(saveOvp, DEFAULT_DEBOUNCE)

// Watcher
watch(activeCase, value => {
  const hasContent = Object.keys(value).length
  if (hasContent) disrupt()
  if (!hasContent) resume()
})

export default function useHealthquestion(v) {
  const { shouldValidate } = useValidation()

  // Computed and methods depending on v
  const invalid = computed(() => {
    return (shouldValidate.value && v.value?.$invalid) || !!v.value?.$errors.length
  })
  const valid = computed(() => {
    return !v.value?.$invalid
  })

  function saveHq(hqId, formData) {
    if (!v.value.$invalid) {
      store(hqId, formData)
    }
  }

  return {
    activeCaseData,
    activeCaseId,
    isActiveModal,
    invalid,
    valid,

    items,

    activeCase,
    tmpDoctors,
    healthQuestionConfig,

    confirmRemoval,
    disrupt,
    editActiveCase,
    forceSave,
    getDoctors,
    resume,
    saveHq,
    setPersonId,
    store,
  }
}
